import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPin, Sunrise, Sunset, Compass, CloudSun, Loader2, Sparkles, Trash2, ExternalLink, ImageIcon, Camera, AlertCircle, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import {
  adminScoutConfig, adminScoutPreview, adminCreateScout, adminListScouts, adminDeleteScout, adminListBookings, adminGetMyGear, getServices,
} from '@/lib/api';
import { formatSessionDate, toISODate } from '@/lib/money';
import { ADMIN_SCOUT } from '@/constants/testIds';

const NONE = '__none__';
const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';

const STATUS_STYLE = {
  ready: 'border-emerald-400/40 text-emerald-400',
  generating: 'border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]',
  failed: 'border-red-400/40 text-red-400',
};

const nextWeekend = () => {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() + ((6 - day + 7) % 7 || 7));
  return toISODate(d);
};

export const AdminScoutTab = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [gear, setGear] = useState(null);
  const [reports, setReports] = useState(null);

  const [form, setForm] = useState({
    location: '', date: nextWeekend(), time: '5:30 PM', occasion: '', service_slug: NONE, subjects: '', notes: '', booking_id: NONE, generate_image: true,
  });
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const previewKey = useRef('');

  const refreshReports = useCallback(() => adminListScouts().then(setReports).catch(() => setReports([])), []);

  useEffect(() => {
    adminScoutConfig().then(setConfig).catch(() => setConfig({}));
    getServices().then(setServices).catch(() => setServices([]));
    adminListBookings({ status: 'booked' }).then(setBookings).catch(() => setBookings([]));
    adminGetMyGear().then(setGear).catch(() => setGear({}));
    refreshReports();
  }, [refreshReports]);

  // Poll while anything is still generating so the list flips to "ready" on its own.
  useEffect(() => {
    if (!reports?.some((r) => r.status === 'generating')) return undefined;
    const t = setInterval(refreshReports, 5000);
    return () => clearInterval(t);
  }, [reports, refreshReports]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const runPreview = useCallback(async () => {
    if (form.location.trim().length < 2 || !form.date) return;
    const key = `${form.location.trim()}|${form.date}|${form.time}`;
    if (key === previewKey.current) return;
    previewKey.current = key;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await adminScoutPreview({ location: form.location.trim(), date: form.date, time: form.time });
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setPreviewError(err?.response?.data?.detail || 'Could not resolve that location.');
    } finally {
      setPreviewLoading(false);
    }
  }, [form.location, form.date, form.time]);

  const applyBooking = (id) => {
    set('booking_id')(id);
    const b = bookings.find((x) => x.id === id);
    if (!b) return;
    setForm((f) => ({
      ...f,
      booking_id: id,
      location: b.location || f.location,
      date: b.session_date || f.date,
      time: b.session_time || f.time,
      occasion: b.package_name || f.occasion,
      service_slug: b.service_slug || f.service_slug,
      subjects: f.subjects || b.client_name,
    }));
    previewKey.current = '';
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (form.location.trim().length < 2) return toast.error('Enter a location first.');
    setGenerating(true);
    try {
      const doc = await adminCreateScout({
        location: form.location.trim(),
        date: form.date,
        time: form.time.trim(),
        occasion: form.occasion.trim(),
        service_slug: form.service_slug === NONE ? null : form.service_slug,
        subjects: form.subjects.trim(),
        notes: form.notes.trim(),
        booking_id: form.booking_id === NONE ? null : form.booking_id,
        generate_image: form.generate_image,
      });
      toast.success('Scouting… your brief will be ready in about a minute.');
      navigate(`/admin/scout/${doc.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start the scout.');
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete scout "${r.title}"?`)) return;
    try {
      await adminDeleteScout(r.id);
      setReports((prev) => prev.filter((x) => x.id !== r.id));
      toast.success('Scout report deleted.');
    } catch {
      toast.error('Could not delete the report.');
    }
  };

  const gearCount = useMemo(() => (gear ? (gear.bodies?.length || 0) + (gear.lenses?.length || 0) + (gear.lighting?.length || 0) : 0), [gear]);
  const solarWindows = preview?.solar?.windows;
  const previewWindows = useMemo(() => (solarWindows || []).filter((w) => w.start_clock && w.end_clock), [solarWindows]);

  return (
    <div className="space-y-6" data-testid={ADMIN_SCOUT.section}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Intelligence</p>
          <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Location Scout</h2>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1 max-w-xl">
            Enter where, when, and what you&apos;re shooting. Scout grounds real sun position, golden hour, and the forecast, then briefs you on light, positions, and starting settings for your kit.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span data-testid={ADMIN_SCOUT.gearHint} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--lxra-border))] px-2.5 py-1 text-[hsl(var(--lxra-muted))]">
            <Camera className="h-3 w-3 text-[hsl(var(--lxra-gold))]" />
            {gearCount > 0 ? `${gearCount} gear items on file` : 'No gear saved — add it in Settings'}
          </span>
          {config && (
            <span data-testid={ADMIN_SCOUT.placesHint} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--lxra-border))] px-2.5 py-1 text-[hsl(var(--lxra-muted))]">
              <ImageIcon className={`h-3 w-3 ${config.places_configured ? 'text-emerald-400' : 'text-[hsl(var(--lxra-muted))]'}`} />
              {config.places_configured ? 'Real reference photos on' : 'Reference photos need a Google key'}
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        {/* FORM */}
        <form onSubmit={handleGenerate} data-testid={ADMIN_SCOUT.form} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5 space-y-4">
          {bookings.length > 0 && (
            <div className="space-y-1.5">
              <Label className={labelClass}>Start from a booking (optional)</Label>
              <Select value={form.booking_id} onValueChange={applyBooking}>
                <SelectTrigger data-testid={ADMIN_SCOUT.bookingSelect} className={fieldClass}><SelectValue placeholder="No booking" /></SelectTrigger>
                <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                  <SelectItem value={NONE}>No booking — scout freely</SelectItem>
                  {bookings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.title} · {formatSessionDate(b.session_date, { month: 'short', day: 'numeric' }) || 'no date'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className={labelClass}>Location</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--lxra-gold))]" />
              <Input
                required
                value={form.location}
                onChange={(e) => set('location')(e.target.value)}
                onBlur={runPreview}
                data-testid={ADMIN_SCOUT.locationInput}
                placeholder="Georgetown Waterfront Park, Washington DC"
                className={`${fieldClass} pl-9`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Date</Label>
              <Input type="date" required value={form.date} onChange={(e) => set('date')(e.target.value)} onBlur={runPreview} data-testid={ADMIN_SCOUT.dateInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Start time</Label>
              <Input value={form.time} onChange={(e) => set('time')(e.target.value)} onBlur={runPreview} data-testid={ADMIN_SCOUT.timeInput} placeholder="5:30 PM (blank = suggest)" className={fieldClass} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Occasion</Label>
              <Input value={form.occasion} onChange={(e) => set('occasion')(e.target.value)} data-testid={ADMIN_SCOUT.occasionInput} placeholder="Couples session" className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Service</Label>
              <Select value={form.service_slug} onValueChange={set('service_slug')}>
                <SelectTrigger data-testid={ADMIN_SCOUT.serviceSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                  <SelectItem value={NONE}>Any</SelectItem>
                  {services.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className={labelClass}>Who&apos;s in frame</Label>
            <Input value={form.subjects} onChange={(e) => set('subjects')(e.target.value)} data-testid={ADMIN_SCOUT.subjectsInput} placeholder="2 adults · family of 5 with a toddler · solo grad" className={fieldClass} />
          </div>

          <div className="space-y-1.5">
            <Label className={labelClass}>Notes for Scout</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set('notes')(e.target.value)} data-testid={ADMIN_SCOUT.notesTextarea} placeholder="Must-have backgrounds, mood, mobility needs, props, anything you already know about the spot…" className={`${fieldClass} h-auto resize-none`} />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] px-3.5 py-2.5">
            <div>
              <p className="text-sm text-[hsl(var(--lxra-fg))]">Concept render</p>
              <p className="text-[11px] text-[hsl(var(--lxra-muted))]">AI visualization of the light and placement (adds ~20s).</p>
            </div>
            <Switch checked={form.generate_image} onCheckedChange={set('generate_image')} data-testid={ADMIN_SCOUT.imageSwitch} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              type="submit"
              disabled={generating || form.location.trim().length < 2}
              data-testid={ADMIN_SCOUT.generateButton}
              className="h-11 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-50"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> Scout this location</>}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => { previewKey.current = ''; runPreview(); }}
              disabled={previewLoading || form.location.trim().length < 2}
              data-testid={ADMIN_SCOUT.previewButton}
              className="h-11 px-4 border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] text-xs uppercase tracking-[0.1em]"
            >
              {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" /> Check sun & weather</>}
            </Button>
          </div>
        </form>

        {/* LIVE PREVIEW */}
        <div className="space-y-4">
          <div data-testid={ADMIN_SCOUT.preview} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
            <p className="text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] flex items-center gap-2">
              <Compass className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> Light check
            </p>
            {previewLoading ? (
              <div className="mt-4 space-y-2">
                <Skeleton className="h-5 w-2/3 bg-[#141416]" />
                <Skeleton className="h-16 w-full bg-[#141416]" />
              </div>
            ) : previewError ? (
              <p data-testid={ADMIN_SCOUT.previewError} className="mt-4 text-sm text-red-300 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {previewError}
              </p>
            ) : !preview ? (
              <p className="mt-4 text-sm text-[hsl(var(--lxra-muted))]">Enter a location and date — sunrise, golden hour, sun direction, and the forecast appear here before you spend a credit.</p>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <p data-testid={ADMIN_SCOUT.previewPlace} className="text-[hsl(var(--lxra-fg))] text-sm font-medium">{preview.place.name}</p>
                  <p className="text-[11px] text-[hsl(var(--lxra-muted))] truncate">{preview.place.address}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl bg-[#141416] p-3 flex items-center gap-2.5">
                    <Sunrise className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
                    <div><p className="text-[hsl(var(--lxra-muted))] text-[10px] uppercase tracking-[0.1em]">Sunrise</p><p data-testid={ADMIN_SCOUT.previewSunrise} className="text-[hsl(var(--lxra-fg))]">{preview.solar.sunrise_clock || '—'}</p></div>
                  </div>
                  <div className="rounded-xl bg-[#141416] p-3 flex items-center gap-2.5">
                    <Sunset className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
                    <div><p className="text-[hsl(var(--lxra-muted))] text-[10px] uppercase tracking-[0.1em]">Sunset</p><p data-testid={ADMIN_SCOUT.previewSunset} className="text-[hsl(var(--lxra-fg))]">{preview.solar.sunset_clock || '—'}</p></div>
                  </div>
                </div>
                <ul className="space-y-1 text-xs">
                  {previewWindows.map((w) => (
                    <li key={w.label} className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-[hsl(var(--lxra-muted))]">
                        <span className={`h-1.5 w-1.5 rounded-full ${w.kind === 'golden' ? 'bg-[hsl(var(--lxra-gold))]' : 'bg-sky-400'}`} />{w.label}
                      </span>
                      <span className="text-[hsl(var(--lxra-fg))]">{w.start_clock} – {w.end_clock}</span>
                    </li>
                  ))}
                </ul>
                {preview.sun_at_session ? (
                  <p data-testid={ADMIN_SCOUT.previewSunAt} className="rounded-xl border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/[0.06] p-3 text-xs text-[hsl(var(--lxra-fg))]">
                    At <strong>{preview.sun_at_session.clock}</strong> the sun sits <strong>{preview.sun_at_session.direction}</strong> at {preview.sun_at_session.elevation}° — {preview.sun_at_session.phase}.
                  </p>
                ) : form.time && preview.time_parsed === false ? (
                  <p className="text-[11px] text-amber-300">Couldn&apos;t read that time — try &quot;5:30 PM&quot; or &quot;17:30&quot;.</p>
                ) : null}
                <div data-testid={ADMIN_SCOUT.previewWeather} className="flex items-start gap-2.5 text-xs text-[hsl(var(--lxra-muted))]">
                  <CloudSun className="h-4 w-4 text-[hsl(var(--lxra-gold))] mt-0.5 shrink-0" />
                  {preview.weather?.available ? (
                    <p><span className="text-[hsl(var(--lxra-fg))]">{preview.weather.summary}</span> · {Math.round(preview.weather.temp_high_f)}° / {Math.round(preview.weather.temp_low_f)}° · {preview.weather.cloud_cover_mean}% cloud · {preview.weather.precip_probability_max}% rain</p>
                  ) : (
                    <p>{preview.weather?.reason || 'Forecast not available yet.'}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PAST REPORTS */}
      <div>
        <h3 className="font-display text-lg tracking-[0.03em] text-[hsl(var(--lxra-fg))] mb-3">Scout Reports</h3>
        {!reports ? (
          <Skeleton className="h-32 rounded-2xl bg-[#141416]" />
        ) : reports.length === 0 ? (
          <div data-testid={ADMIN_SCOUT.empty} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center text-sm text-[hsl(var(--lxra-muted))]">
            No scout reports yet. Your first brief is one form away.
          </div>
        ) : (
          <ul data-testid={ADMIN_SCOUT.list} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {reports.map((r) => (
              <li key={r.id} data-testid={ADMIN_SCOUT.row(r.id)} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-4 flex flex-col gap-3 hover:border-[hsl(var(--lxra-gold))]/40 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-[hsl(var(--lxra-fg))] truncate">{r.place?.name || r.request?.location}</p>
                    <p className="text-[11px] text-[hsl(var(--lxra-muted))] truncate">
                      {r.request?.occasion || 'Session'} · {formatSessionDate(r.request?.date, { month: 'short', day: 'numeric' })}{r.request?.time ? ` · ${r.request.time}` : ''}
                    </p>
                  </div>
                  <Badge data-testid={ADMIN_SCOUT.rowStatus(r.id)} variant="outline" className={`text-[10px] gap-1 shrink-0 ${STATUS_STYLE[r.status] || ''}`}>
                    {r.status === 'generating' && <Loader2 className="h-3 w-3 animate-spin" />}
                    {r.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-[hsl(var(--lxra-muted))]">
                  {r.solar?.sunset_clock && <span className="inline-flex items-center gap-1"><Sunset className="h-3 w-3" /> {r.solar.sunset_clock}</span>}
                  {r.sun_at_session && <span className="inline-flex items-center gap-1"><Compass className="h-3 w-3" /> {r.sun_at_session.direction}</span>}
                  {r.booking_id && <span className="text-[hsl(var(--lxra-gold))]">Pinned</span>}
                </div>
                <div className="mt-auto flex items-center justify-between gap-2">
                  <Link
                    to={`/admin/scout/${r.id}`}
                    data-testid={ADMIN_SCOUT.openButton(r.id)}
                    className="inline-flex items-center h-8 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-[11px] uppercase tracking-[0.08em] font-semibold transition-colors"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open brief
                  </Link>
                  <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={ADMIN_SCOUT.deleteButton(r.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
